import { Survivor } from './CampManager';
import { ResourceManager, ResourceStock, ResourceType } from './ResourceManager';
import { UpgradeManager } from './UpgradeManager';
import { ExpeditionId, EXPEDITION_DEFINITIONS, Range, getAvailableExpeditions } from '../data/expeditions';
import { CLASS_DEFINITIONS } from '../constants';
import { LOOT_TABLES, ITEM_DEFINITIONS } from '../data/items';

// === GESTIONNAIRE D'EXPÉDITIONS IDLE ===
// Les survivants partent en expédition pendant le run du joueur.
// Résultat calculé au retour. Risque de mort (permadeath).

const STORAGE_KEY = 'deadtide_expedition';

/** Expédition en cours (sauvegardée en localStorage) */
export interface ActiveExpedition {
  expeditionId: ExpeditionId;
  /** IDs des survivants envoyés */
  survivorIds: string[];
}

/** Résultat d'un survivant dans l'expédition */
export interface SurvivorExpeditionResult {
  survivorId: string;
  survivorName: string;
  alive: boolean;
  /** Items récupérés en cas de mort (50/50 par item) */
  recoveredItems?: string[];
  lostItems?: string[];
}

/** Résultat complet d'une expédition */
export interface ExpeditionResult {
  expeditionId: ExpeditionId;
  /** Ressources obtenues */
  resources: Partial<ResourceStock>;
  /** Or obtenu */
  gold: number;
  /** Item rare trouvé (definitionId ou null) */
  itemFound: string | null;
  /** Résultat par survivant */
  survivors: SurvivorExpeditionResult[];
  /** Nombre de morts */
  deaths: number;
  /**
   * Sprint 85 : level-ups appliqués aux survivants à la résolution.
   * Vide si l'expédition n'a pas été un succès complet (zero deaths).
   */
  levelUps?: Array<{ survivorId: string; survivorName: string; oldLevel: number; newLevel: number }>;
}

export class ExpeditionManager {
  private activeExpedition: ActiveExpedition | null;

  constructor() {
    this.activeExpedition = this.load();
  }

  // === ACCÈS ===

  /** Expédition en cours */
  getActive(): ActiveExpedition | null {
    return this.activeExpedition;
  }

  /** Vérifie si une expédition est en cours */
  isOnExpedition(): boolean {
    return this.activeExpedition !== null;
  }

  /** Vérifie si un survivant est en expédition */
  isSurvivorOnExpedition(survivorId: string): boolean {
    return this.activeExpedition?.survivorIds.includes(survivorId) ?? false;
  }

  /** Expéditions disponibles selon le niveau de la vigie */
  getAvailable(upgrades: UpgradeManager) {
    const vigieLevel = upgrades.getBuildingLevel('watchtower');
    return getAvailableExpeditions(vigieLevel);
  }

  // === LANCEMENT ===

  /**
   * Calcule le score d'un survivant.
   * Sprint 85 : inclut un bonus de niveau (+5 par niveau au-dessus de 1).
   * Niveau 1 = +0, Niveau 5 = +20.
   */
  getSurvivorScore(survivor: Survivor): number {
    const classDef = CLASS_DEFINITIONS[survivor.class];
    const stats = classDef.stats;
    const levelBonus = Math.max(0, (survivor.level - 1)) * 5;
    return stats.maxHp + stats.attack * 2 + stats.defense * 2 + levelBonus;
  }

  /** Calcule le score total de l'équipe */
  getTeamScore(survivors: Survivor[]): number {
    return survivors.reduce((sum, s) => sum + this.getSurvivorScore(s), 0);
  }

  /** Calcule le risque final de mort (%) pour une expédition avec cette équipe */
  getDeathRisk(expeditionId: ExpeditionId, survivors: Survivor[]): number {
    const def = EXPEDITION_DEFINITIONS[expeditionId];
    if (def.deathRisk.max === 0) return 0;

    const teamScore = this.getTeamScore(survivors);
    const baseRisk = (def.deathRisk.min + def.deathRisk.max) / 2;
    const reduction = Math.min(1, teamScore / def.scoreThreshold) * 0.7;
    const finalRisk = baseRisk * (1 - reduction);
    // Ne descend jamais en dessous de 30% du risque min
    return Math.max(def.deathRisk.min * 0.3, finalRisk);
  }

  /**
   * Lance une expédition.
   * Retourne true si lancée avec succès.
   */
  startExpedition(expeditionId: ExpeditionId, survivorIds: string[]): boolean {
    if (this.activeExpedition) return false;

    const def = EXPEDITION_DEFINITIONS[expeditionId];
    if (!def) return false;
    if (survivorIds.length < def.minSurvivors || survivorIds.length > def.maxSurvivors) return false;

    this.activeExpedition = { expeditionId, survivorIds };
    this.save();
    return true;
  }

  /**
   * Résout l'expédition au retour du run.
   * Retourne le résultat ou null si aucune expédition.
   */
  resolveExpedition(allSurvivors: Survivor[]): ExpeditionResult | null {
    if (!this.activeExpedition) return null;

    const { expeditionId, survivorIds } = this.activeExpedition;
    const def = EXPEDITION_DEFINITIONS[expeditionId];

    // Récupérer les survivants envoyés (y compris ceux morts en combat)
    const survivorResults: SurvivorExpeditionResult[] = [];
    let deaths = 0;
    const aliveSent: Survivor[] = [];

    for (const id of survivorIds) {
      const s = allSurvivors.find(sv => sv.id === id);
      if (!s || !s.alive) {
        // Survivant mort en combat ou supprimé → considéré mort en expédition aussi
        deaths++;
        survivorResults.push({
          survivorId: id,
          survivorName: s?.name ?? 'Inconnu',
          alive: false,
        });
      } else {
        aliveSent.push(s);
      }
    }

    // Calculer les récompenses
    const resources: Partial<ResourceStock> = {};
    for (const reward of def.resources) {
      const chance = reward.chance ?? 1;
      if (Math.random() < chance) {
        const amount = this.randomRange(reward.amount);
        resources[reward.type] = (resources[reward.type] ?? 0) + amount;
      }
    }

    const gold = this.randomRange(def.gold);

    // Item rare
    let itemFound: string | null = null;
    if (def.itemChance > 0 && Math.random() < def.itemChance) {
      const rareItems = LOOT_TABLES.rare;
      itemFound = rareItems[Math.floor(Math.random() * rareItems.length)];
    }

    // Risque de mort par survivant vivant
    const deathRisk = this.getDeathRisk(expeditionId, aliveSent);

    for (const s of aliveSent) {
      const died = Math.random() * 100 < deathRisk;
      if (died) {
        deaths++;
        survivorResults.push({
          survivorId: s.id,
          survivorName: s.name,
          alive: false,
        });
      } else {
        survivorResults.push({
          survivorId: s.id,
          survivorName: s.name,
          alive: true,
        });
      }
    }

    // Si des morts, réduire les ressources partiellement (garder 60%)
    if (deaths > 0 && deaths === survivorIds.length) {
      for (const type of Object.keys(resources) as ResourceType[]) {
        resources[type] = Math.ceil((resources[type] ?? 0) * 0.6);
      }
    }

    // Sprint 85 — Level-ups préparés pour les survivants vivants si zéro mort
    // (le level up effectif est appliqué par ExpeditionResultScene via CampManager)
    const levelUps: Array<{ survivorId: string; survivorName: string; oldLevel: number; newLevel: number }> = [];
    if (deaths === 0) {
      for (const sr of survivorResults) {
        if (!sr.alive) continue;
        const s = allSurvivors.find(sv => sv.id === sr.survivorId);
        if (!s || s.level >= 5) continue;
        levelUps.push({
          survivorId: s.id,
          survivorName: s.name,
          oldLevel: s.level,
          newLevel: s.level + 1,
        });
      }
    }

    // Nettoyer l'expédition
    this.activeExpedition = null;
    this.save();

    return {
      expeditionId,
      resources,
      gold,
      itemFound,
      survivors: survivorResults,
      deaths,
      levelUps,
    };
  }

  // === HELPERS ===

  private randomRange(range: Range): number {
    return Math.floor(Math.random() * (range.max - range.min + 1)) + range.min;
  }

  // === PERSISTENCE ===

  private save(): void {
    try {
      if (this.activeExpedition) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this.activeExpedition));
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      // localStorage plein ou indisponible
    }
  }

  private load(): ActiveExpedition | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const data = JSON.parse(raw);
        if (data.expeditionId && Array.isArray(data.survivorIds)) {
          return data as ActiveExpedition;
        }
      }
    } catch {
      // données corrompues
    }
    return null;
  }

  /** Reset (debug) */
  resetAll(): void {
    this.activeExpedition = null;
    this.save();
  }

  /**
   * Annule l'expédition en cours (rappel des survivants).
   * Aucune récompense. Les survivants redeviennent disponibles.
   * Retourne true si une expédition a bien été annulée.
   */
  cancelExpedition(): boolean {
    if (!this.activeExpedition) return false;
    this.activeExpedition = null;
    this.save();
    return true;
  }
}
